import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  ArrowLeft,
  User,
  Mail,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader,
  Info,
  Key
} from 'lucide-react';

const UserForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    email: '',
    full_name: '',
    role: 'visualizador',
    team: ''
  });

  const roles = [
    { value: 'admin', label: 'Administrador', description: 'Acceso total al sistema' },
    { value: 'tesorero', label: 'Tesorero', description: 'Gestión financiera y visualización' },
    { value: 'entrenador', label: 'Entrenador', description: 'Gestión de jugadores y entrenamientos' },
    { value: 'visualizador', label: 'Visualizador', description: 'Solo lectura' }
  ];

  const teams = ['General', 'Juvenil', 'Cadete', 'Amateur'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validaciones
      if (!formData.id || !formData.email || !formData.full_name) {
        throw new Error('UUID, email y nombre completo son obligatorios');
      }

      // Validar formato UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(formData.id)) {
        throw new Error('El UUID no tiene un formato válido');
      }

      if (formData.role === 'entrenador' && !formData.team) {
        throw new Error('Los entrenadores deben tener un equipo asignado');
      }

      // Verificar si el UUID ya existe
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('id', formData.id)
        .single();

      if (existingUser) {
        throw new Error('Ya existe un usuario con este UUID');
      }

      const userData = {
        id: formData.id,
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        team: formData.team || null,
        active: true
      };

      const { error: insertError } = await supabase
        .from('users')
        .insert([userData]);

      if (insertError) throw insertError;

      setSuccess(true);

      setTimeout(() => {
        navigate('/users');
      }, 2000);

    } catch (err) {
      console.error('Error al crear usuario:', err);
      setError(err.message || 'Error al crear el usuario');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Usuario creado!</h2>
          <p className="text-gray-600 mb-6">El usuario ya puede iniciar sesión en el sistema</p>
          <button
            onClick={() => navigate('/users')}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Volver a Usuarios
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/users')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Nuevo Usuario</h1>
              <p className="text-sm text-gray-600">Crea un nuevo usuario del sistema</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instrucciones PRIMERO */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-2">
                📋 Pasos para crear un usuario
              </h3>
              <ol className="space-y-2 text-sm text-red-900">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <div>
                    Ve a <strong>Supabase</strong> → <strong>Authentication</strong> → <strong>Users</strong> → <strong>"Add user"</strong>
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <div>
                    Rellena el email y contraseña, marca <strong>"Auto Confirm User"</strong> ✅
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <div>
                    Click en <strong>"Create user"</strong>
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <div>
                    <strong>Copia el UUID</strong> que aparece en la columna "UID"
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">5.</span>
                  <div>
                    <strong>Vuelve aquí</strong> y pega el UUID en el formulario de abajo
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8">
          <div className="space-y-6">
            {/* UUID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                UUID del usuario de Supabase <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                required
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Copia el UUID desde Supabase Authentication después de crear el usuario
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="usuario@ejemplo.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Debe ser el mismo email usado en Supabase Authentication
              </p>
            </div>

            {/* Nombre completo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                placeholder="Juan García López"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="w-4 h-4 inline mr-1" />
                Rol <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Equipo (solo si es entrenador) */}
            {formData.role === 'entrenador' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equipo asignado <span className="text-red-500">*</span>
                </label>
                <select
                  name="team"
                  value={formData.team}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Selecciona un equipo</option>
                  {teams.filter(t => t !== 'General').map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  El entrenador solo podrá ver y gestionar este equipo
                </p>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader className="w-4 h-4 animate-spin" />}
              {loading ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;